import type {
  FixedRoute,
  FixedRouteSegment,
  FixedRouteStop,
  FixedRouteStopConfig,
} from '../../types/fixedRoutes';
import type { NetworkRouteDetail, RouteShape } from '../../services/network/networkApi';
import { computeRouteGeometryHash } from './routeToLineConverter';

/**
 * Convert a hosted-network route (shape + optional detail) into an **editable fork**:
 * a real `FixedRoute` (+ its stops) tagged with `networkOrigin` provenance (PRD-04 FR15/FR16).
 *
 * The live feed route stays read-only in the Network tab; this copy is fully editable and
 * lives in `FixedRoutesContext.routes` like any authored route. Geometry is taken straight
 * from the feed shape (already `[lat, lng]`, matching `RouteGeometry.coordinates`) and the
 * copy is flagged `isManuallyEdited` so the map-sync doesn't re-route over the real alignment.
 */

const DEFAULT_WAIT_MINUTES = 0;
const DEFAULT_SEGMENT_MINUTES = 5; // matches RouteBuilder's default leg time
const DEFAULT_START_TIME = '08:00';
const DEFAULT_END_TIME = '18:00';

export interface NetworkForkResult {
  route: FixedRoute;
  stops: FixedRouteStop[];
}

function normalizeColor(color?: string | number | null): string | undefined {
  // route_color may arrive as a number (DuckDB CSV type-inference) — coerce, don't assume string.
  if (color === null || color === undefined || color === '') return undefined;
  const raw = String(color).trim();
  const c = raw.startsWith('#') ? raw : `#${raw}`;
  return /^#[0-9a-fA-F]{6}$/.test(c) ? c : undefined;
}

export function networkRouteToFixedRoute(
  feedId: string,
  version: string | null,
  shape: RouteShape,
  detail: NetworkRouteDetail | null,
  forkedAt: number,
): NetworkForkResult {
  // Prefix stop ids with the feed so copies from the same network share/merge stops
  // (bulkAddStopsRoutes merges by id) without colliding with the user's own stops.
  const stopIdFor = (networkStopId: string) => `gtfs-${feedId}-${networkStopId}`;

  // A BODS-origin feed uses the NaPTAN ATCO code as its stop_id, so keep it on the fork
  // (as atcoCode). The BODS/TransXChange export then emits a real StopPointRef instead of a
  // synthetic CUSTOM- ref. Non-ATCO ids (e.g. Transitland feeds) leave atcoCode unset.
  const isAtco = (id: string) => /^[0-9]{3}[0-9A-Za-z]{5,9}$/.test(id);

  const stops: FixedRouteStop[] = shape.stops.map((s) => ({
    id: stopIdFor(s.stopId),
    name: s.stopName || s.stopId,
    coordinates: { lat: s.stopLat, lng: s.stopLon },
    externalStopReferenceId: s.stopId,
    ...(isAtco(s.stopId) ? { atcoCode: s.stopId } : {}),
  }));

  const stopConfigs: FixedRouteStopConfig[] = stops.map((s) => ({
    stopId: s.id,
    waitTimeMinutes: DEFAULT_WAIT_MINUTES,
  }));

  const segments: FixedRouteSegment[] = stopConfigs.slice(0, -1).map((s, i) => ({
    fromStopId: s.stopId,
    toStopId: stopConfigs[i + 1].stopId,
    travelTimeMinutes: DEFAULT_SEGMENT_MINUTES,
  }));

  // Coerce to string — network API fields are loosely typed at runtime, and a non-string
  // name later crashes `routeName.trim()` in RouteBuilder.
  const rawName = detail?.routeLongName || shape.routeShortName || detail?.routeShortName;
  const name = rawName != null && rawName !== '' ? String(rawName) : `Route ${shape.routeId}`;
  const rawShortName = shape.routeShortName || detail?.routeShortName;
  const color = normalizeColor(shape.routeColor) ?? normalizeColor(detail?.routeColor);
  const hasGeometry = shape.coordinates.length >= 2;

  const route: FixedRoute = {
    id: `route-net-${shape.routeId}-${forkedAt}`,
    name,
    shortName: rawShortName != null && rawShortName !== '' ? String(rawShortName) : undefined,
    startTime: DEFAULT_START_TIME,
    endTime: DEFAULT_END_TIME,
    stops: stopConfigs,
    segments,
    color,
    gtfsRouteId: shape.routeId,
    gtfsRouteType: shape.routeType ?? detail?.routeType ?? undefined,
    // stopsHash is stamped below via computeRouteGeometryHash — the same hash the map-sync
    // and convertRouteToLine recompute to validate the cache. A bespoke token here never
    // matched, so the cached GTFS shape was always treated as stale and every fork got fully
    // re-routed through OSRM (driving profile) on first render — slow, and wrong for rail.
    geometry: hasGeometry
      ? {
          coordinates: shape.coordinates,
          calculatedAt: forkedAt,
          stopsHash: '',
        }
      : undefined,
    // Keep the feed alignment; don't let stop edits trigger an automatic reroute over it
    // until the user deliberately changes the geometry.
    isManuallyEdited: true,
    networkOrigin: {
      feedId,
      version: version ?? undefined,
      gtfsRouteId: shape.routeId,
      forkedAt,
    },
  };

  // Stamp the cache hash now that the route exists, so the stored GTFS shape is recognized as
  // valid on first render (matches computeRouteGeometryHash in the map-sync / convertRouteToLine).
  // The fork has no waypoints and isn't bus/circulator/freehand, so the consumers recompute the
  // identical hash from the same stops in the same order.
  if (route.geometry) {
    route.geometry.stopsHash = computeRouteGeometryHash(route, stops);
  }

  return { route, stops };
}

/** Find an existing editable copy of a live-network route, if one was already forked. */
export function findExistingFork(
  routes: FixedRoute[],
  feedId: string,
  gtfsRouteId: string,
): FixedRoute | undefined {
  return routes.find(
    (r) => r.networkOrigin?.feedId === feedId && r.networkOrigin?.gtfsRouteId === gtfsRouteId,
  );
}
